import { enUS, enGB, enAU, enIN, es, frCA, zhCN, ptBR, ko, vi, hi, te, ta, kn } from 'date-fns/locale';
import { registerLocale } from 'react-datepicker';

/**
 * Singapore Mandarin date widths per CLDR `zh-SG`: every width is 年月日, unlike
 * zh-CN which drops to `yyyy-MM-dd` / `yy-MM-dd` for medium and short.
 */
const ZH_SG_DATE_FORMATS: Record<string, string> = {
  full: "y'年'M'月'd'日' EEEE",
  long: "y'年'M'月'd'日'",
  medium: "y'年'M'月'd'日'",
  short: "y'年'M'月'd'日'",
};

/**
 * date-fns ships no zh-SG locale. Singapore Mandarin shares zh-CN's wording (so
 * formatDistance / localize / match are reused verbatim) but NOT its date
 * conventions: CLDR gives zh-SG a Sunday week start and 年月日 at every width,
 * against zh-CN's Monday start and numeric medium/short. Deriving rather than
 * aliasing keeps relative time identical while fixing the calendar behaviour.
 */
const zhSG: typeof zhCN = {
  ...zhCN,
  code: 'zh-SG',
  formatLong: {
    ...zhCN.formatLong,
    date: (options) => ZH_SG_DATE_FORMATS[options?.width ?? 'full'] ?? ZH_SG_DATE_FORMATS.full,
  },
  options: {
    ...zhCN.options,
    weekStartsOn: 0, // CLDR zh-SG firstDay = Sunday (zh-CN is Monday)
    firstWeekContainsDate: 1, // pairs with a Sunday start; zh-CN uses the ISO 4
  },
};

/**
 * Singapore English clock widths per CLDR `en-SG`: 12-hour, unlike en-GB's 24-hour.
 * Date widths are NOT listed because en-GB already matches en-SG at every width
 * (dd/MM/yyyy, d MMM yyyy, d MMMM yyyy), so they are inherited unchanged.
 */
const EN_SG_TIME_FORMATS: Record<string, string> = {
  full: 'h:mm:ss a zzzz',
  long: 'h:mm:ss a z',
  medium: 'h:mm:ss a',
  short: 'h:mm a',
};

/**
 * date-fns ships no en-SG locale. Singapore English shares en-GB's day-month-year
 * date order (so the date patterns and all wording are reused verbatim) but not its
 * clock or calendar: CLDR gives en-SG a 12-hour clock and a Sunday week start,
 * against en-GB's 24-hour and Monday. Deriving fixes both without falling back to
 * en-US, which would wrongly flip dates to month-first.
 */
const enSG: typeof enGB = {
  ...enGB,
  code: 'en-SG',
  formatLong: {
    ...enGB.formatLong,
    time: (options) => EN_SG_TIME_FORMATS[options?.width ?? 'full'] ?? EN_SG_TIME_FORMATS.full,
  },
  options: {
    ...enGB.options,
    weekStartsOn: 0, // CLDR en-SG firstDay = Sunday (en-GB is Monday)
    firstWeekContainsDate: 1, // pairs with a Sunday start; en-GB uses the ISO 4
  },
};

/**
 * Map an app UI locale (see ./languages) to a date-fns locale so relative-time
 * helpers — formatDistanceToNow / formatDistance — render in the user's language
 * (e.g. "hace 2 minutos", "il y a 4 semaines", "约 1 个月前") instead of English.
 *
 * date-fns ships no Mexico-specific Spanish, so es-MX falls back to generic `es`
 * (correct for relative time). Anything unknown falls back to English.
 *
 * date-fns 3.6 ships no Malayalam, so ml-IN uses en-IN (Indian day-month-year
 * order, English relative time) rather than en-US's month-first dates.
 *
 * en-GB and en-AU are used directly (unlike en-SG above) — date-fns ships both
 * natively with the correct day-month-year date order, so no derivation is needed.
 *
 * Pass the component-scoped i18n.language (from useTranslation) — NOT the global
 * singleton — so the English-pinned standalone Aria app stays English.
 */
const DATE_FNS_LOCALES: Record<string, typeof enUS> = {
  'en-US': enUS,
  'en-SG': enSG,
  'en-GB': enGB,
  'en-AU': enAU,
  'es-MX': es,
  'fr-CA': frCA,
  'zh-CN': zhCN,
  'zh-SG': zhSG,
  'pt-BR': ptBR,
  'ko-KR': ko,
  'vi-VN': vi,
  'en-IN': enIN,
  'hi-IN': hi,
  'te-IN': te,
  'ta-IN': ta,
  'kn-IN': kn,
  'ml-IN': enIN,
};

/** react-datepicker locale keys (must match registerLocale names). */
const PICKER_LOCALE_KEYS: Record<string, string> = {
  'en-US': 'en-US',
  'en-SG': 'en-SG',
  'en-GB': 'en-GB',
  'en-AU': 'en-AU',
  'es-MX': 'es',
  'fr-CA': 'fr-CA',
  'zh-CN': 'zh-CN',
  'zh-SG': 'zh-SG',
  'pt-BR': 'pt-BR',
  'ko-KR': 'ko',
  'vi-VN': 'vi',
  'en-IN': 'en-IN',
  'hi-IN': 'hi',
  'te-IN': 'te',
  'ta-IN': 'ta',
  'kn-IN': 'kn',
  'ml-IN': 'en-IN',
};

let pickerLocalesRegistered = false;

export function ensureDatePickerLocalesRegistered(): void {
  if (pickerLocalesRegistered) return;
  registerLocale('en-US', enUS);
  registerLocale('en-SG', enSG);
  registerLocale('en-GB', enGB);
  registerLocale('en-AU', enAU);
  registerLocale('es', es);
  registerLocale('fr-CA', frCA);
  registerLocale('zh-CN', zhCN);
  registerLocale('zh-SG', zhSG);
  registerLocale('pt-BR', ptBR);
  registerLocale('ko', ko);
  registerLocale('vi', vi);
  registerLocale('en-IN', enIN);
  registerLocale('hi', hi);
  registerLocale('te', te);
  registerLocale('ta', ta);
  registerLocale('kn', kn);
  pickerLocalesRegistered = true;
}

export function getDateFnsLocale(language?: string): typeof enUS {
  return (language && DATE_FNS_LOCALES[language]) || enUS;
}

/** Locale key to pass to react-datepicker `locale` prop. */
export function getDatePickerLocale(language?: string): string {
  ensureDatePickerLocalesRegistered();
  if (language && PICKER_LOCALE_KEYS[language]) return PICKER_LOCALE_KEYS[language]!;
  return 'en-US';
}
